import { Suspense, useEffect, useState } from 'react'
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useParams,
} from 'react-router-dom'
import { AuthProvider } from '@core/auth/AuthContext'
import { useAuth } from '@core/auth/useAuth'
import LoginPage from '@core/auth/LoginPage'
import SetPasswordPage from '@core/auth/SetPasswordPage'
import AcessoBloqueado from '@core/auth/AcessoBloqueado'
import AdminPage from '@core/admin/AdminPage'
import PaginaNotificacoes from '@core/notificacoes/PaginaNotificacoes'
import NoMeuWorkspace from '@core/notificacoes/NoMeuWorkspace'
import ViewerShell from '@core/ViewerShell'
import WorkspaceLayout from '@core/layout/WorkspaceLayout'
import ViewerPage from '@viewer/ViewerPage'
import { moduleRegistry } from '@modules/registry'
import PartilhadosComigoPage from '@core/partilhas/PartilhadosComigoPage'
import { modulosNaInterface } from '@core/partilhas/partilhasRegras'
import { textoDoArranque } from '@core/auth/textoDoArranque'

/**
 * Splash while the boot probe (`/api/auth/me`) is in flight. Normalmente
 * some em menos de um segundo; com o servidor a dormir (plano gratuito
 * do Render) o primeiro pedido levou 42 s a 16/10/2026, e a página só
 * dizia "A carregar…". Passados uns segundos diz o que se passa.
 */
function BootSplash() {
  const [segundos, setSegundos] = useState(0)
  useEffect(() => {
    const inicio = Date.now()
    const t = window.setInterval(() => setSegundos(Math.floor((Date.now() - inicio) / 1000)), 1000)
    return () => window.clearInterval(t)
  }, [])
  const { titulo, detalhe } = textoDoArranque(segundos)
  return (
    <div
      className="flex h-full flex-col items-center justify-center gap-2 bg-[var(--color-bg)] px-4 text-center text-sm text-[var(--color-text-muted)]"
      role="status"
      data-testid="arranque"
    >
      <span>{titulo}</span>
      {detalhe && <span className="max-w-md text-xs text-[var(--color-text-dim)]">{detalhe}</span>}
    </div>
  )
}

function ModuleSuspenseFallback() {
  return (
    <div className="flex min-w-0 flex-1 items-center justify-center text-sm text-[var(--color-text-muted)]">
      A carregar módulo…
    </div>
  )
}

/**
 * Workspace gate: confirms the URL workspace slug matches the session's
 * workspace before rendering the workspace layout. Wrong slug → bounce
 * to the correct one. (Multi-workspace switching is post-MVP.)
 */
function WorkspaceRoutes() {
  const { workspaceSlug } = useParams<{ workspaceSlug: string }>()
  const { workspace, modules, partilhados } = useAuth()
  if (workspace && workspace.slug !== workspaceSlug) {
    return <Navigate to={`/${workspace.slug}/`} replace />
  }
  // Quem tem um BCF ou uma triagem partilhados de outro workspace vê
  // as páginas precisas para os abrir; o servidor verifica cada recurso.
  const enabled = new Set(modulosNaInterface(modules, partilhados))
  return (
    <Routes>
      <Route element={<WorkspaceLayout />}>
        <Route index element={<ViewerPage />} />
        <Route path="account/password" element={<SetPasswordPage />} />
        <Route path="admin" element={<AdminPage />} />
        <Route path="notificacoes" element={<PaginaNotificacoes />} />
        <Route path="partilhados" element={<PartilhadosComigoPage />} />
        {moduleRegistry
          .filter((m) => enabled.has(m.slug))
          .map((m) => {
            const Page = m.page
            return (
              <Route
                key={m.slug}
                path={`${m.slug}/*`}
                element={
                  <Suspense fallback={<ModuleSuspenseFallback />}>
                    <Page />
                  </Suspense>
                }
              />
            )
          })}
        <Route path="*" element={<Navigate to="" replace />} />
      </Route>
    </Routes>
  )
}

function AppRoutes() {
  const { mode, workspace } = useAuth()

  if (mode === 'loading') {
    return <BootSplash />
  }

  // Local-first: backend unreachable. Render the viewer at any path
  // without auth or workspace concept; URL is ignored.
  if (mode === 'local') {
    return <ViewerShell />
  }

  // O servidor respondeu mas não deixa entrar (403) ou falhou (5xx).
  if (mode === 'forbidden' || mode === 'server-error') {
    return <AcessoBloqueado />
  }

  return (
    <Routes>
      <Route
        path="/login"
        element={
          mode === 'cloud' && workspace ? (
            <Navigate to={`/${workspace.slug}/`} replace />
          ) : (
            <LoginPage />
          )
        }
      />
      <Route
        path="/admin"
        element={
          mode === 'cloud' && workspace ? (
            <Navigate to={`/${workspace.slug}/admin`} replace />
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />
      <Route
        path="/account/password"
        element={
          mode === 'cloud' && workspace ? (
            <Navigate to={`/${workspace.slug}/account/password`} replace />
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />
      {/* Ligações das notificações para quem vê um recurso a partir de
          outro workspace: o ~ é o workspace da sessão. */}
      <Route path="/~/*" element={<NoMeuWorkspace />} />
      <Route
        path="/:workspaceSlug/*"
        element={
          mode === 'cloud' ? (
            <WorkspaceRoutes />
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />
      <Route
        path="/"
        element={
          mode === 'cloud' && workspace ? (
            <Navigate to={`/${workspace.slug}/`} replace />
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  )
}

export default App
